import React from 'react';
import Button from '../../../../composants/Button/Button';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/export.svg';
import '../../../../composants/DocumentsList/DocumentsList.css';
import './DetailCandidature.css';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ValidationItem {
  label: string;
  ok: boolean | null;
  detail?: string;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface SectionSoumissionProps {
  isSoumise: boolean;
  deposee_at?: string | null;
  estMandataire: boolean;
  validationItems: ValidationItem[];
  isDeposing: boolean;
  acceptConditions: boolean;
  onToggleAccept: () => void;
  onOpenCguModal?: () => void;
  cguUrl?: string; // Lien URL direct pour les CGU (démo)
  /** Texte avant le lien CGU. Défaut : "Je certifie que les informations fournies sont exactes et complètes, et j'accepte les" */
  cguCheckboxPrefix?: string;
  /** Texte du lien CGU. Défaut : "conditions de l'accord-cadre 2027" */
  cguLinkText?: string;
  onDeposer: () => void;
  onPreviewPdf?: () => void;
  isGeneratingPdf?: boolean;
  pdfUrl?: string | null;
  nomSignataire: string;
  onNomSignataireChange: (val: string) => void;
}

// ── Section "Révision + Soumission" ──────────────────────────────────────────

const SectionSoumission: React.FC<SectionSoumissionProps> = ({
  isSoumise,
  deposee_at,
  estMandataire,
  validationItems,
  isDeposing,
  acceptConditions,
  onToggleAccept,
  onOpenCguModal,
  cguUrl,
  cguCheckboxPrefix,
  cguLinkText,
  onDeposer,
  onPreviewPdf,
  isGeneratingPdf,
  pdfUrl,
  nomSignataire,
  onNomSignataireChange,
}) => {
  if (isSoumise) {
    const dateDepot = deposee_at
      ? new Date(deposee_at).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
      : null;

    return (
      <>
        <div className="section-content">
          <div className="soumission-badge-soumise">
            Offre déposée avec succès
            {dateDepot && <div style={{ fontSize: '0.85em', marginTop: '4px', fontWeight: 400 }}>Déposée le {dateDepot}</div>}
          </div>

          {/* PDF visible après dépôt */}
          <div id="pdf-preview-zone">
            {isGeneratingPdf && (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: '#6b7280' }}>
                <span className="bottom-save-spinner" style={{ display: 'inline-block', marginRight: '0.5rem' }} />
                Génération du contrat en cours…
              </div>
            )}
            {pdfUrl && !isGeneratingPdf && (
              <div style={{ marginTop: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Contrat déposé</span>
                  <Button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = pdfUrl;
                      link.download = 'candidature.pdf';
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    }}
                    icon={<DownloadIcon className="dl-button-icon-white" />}
                    value="Télécharger"
                    size="small"
                  />
                </div>
                <iframe src={pdfUrl} title="PDF candidature" width="100%" height="500px" style={{ display: 'block', border: 'none' }} />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  if (!estMandataire) {
    const allOk = validationItems.every((item) => item.ok === true);
    // Co-traitant : afficher uniquement le bouton de prévisualisation PDF
    return (
      <>
        <div className="section-content soumission-section">
          <p style={{ color: '#6b7280', fontStyle: 'italic', marginBottom: '0.75rem' }}>
            Seul le mandataire peut déposer la candidature.
          </p>
        </div>
      </>
    );
  }

  const allOk = validationItems.every((item) => item.ok === true);
  const canDeposer = acceptConditions && allOk && !isDeposing;

  return (
    <>
      <div className="section-content soumission-section">

        {/* Nom du signataire (EPIC-50) */}
        <div className="soumission-block">
          <p className="soumission-block-title">Nom du signataire</p>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
            Ce nom apparaîtra sur le contrat comme signataire. Limité à 30 caractères.
          </p>
          <input
            type="text"
            value={nomSignataire}
            onChange={(e) => onNomSignataireChange(e.target.value)}
            maxLength={30}
            placeholder="Prénom NOM"
            className="nom-signataire-input"
            disabled={isSoumise}
          />
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
            {nomSignataire.length}/30
          </div>
        </div>

        {/* Checklist pré-soumission */}
        <div className="soumission-block">
          <p className="soumission-block-title">Conditions requises avant dépôt</p>
          <ul className="soumission-checklist">
            {validationItems.map((item) => (
              <li
                key={item.label}
                className={`soumission-check-item ${item.ok === null ? 'loading' : item.ok ? 'ok' : 'ko'}`}
              >
                <span className="soumission-check-icon">
                  {item.ok === null ? '…' : item.ok ? '✓' : '✗'}
                </span>
                <span className="soumission-check-label">{item.label}</span>
                {item.detail && (
                  <span className="soumission-check-detail">{item.detail}</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Checkbox conditions */}
        <label className="soumission-conditions">
          <input
            type="checkbox"
            checked={acceptConditions}
            onChange={() => {
              if (!acceptConditions) {
                onOpenCguModal ? onOpenCguModal() : onToggleAccept();
              } else {
                onToggleAccept();
              }
            }}
            disabled={!allOk}
          />
          <span>
            {cguCheckboxPrefix ?? "Je certifie que les informations fournies sont exactes et complètes, et j'accepte les"}{' '}
            {cguUrl ? (
              <a href={cguUrl} target="_blank" rel="noopener noreferrer" className="cgu-link">
                {cguLinkText ?? "conditions de l'accord-cadre 2027"}
              </a>
            ) : onOpenCguModal ? (
              <button type="button" className="cgu-link" onClick={onOpenCguModal}>
                {cguLinkText ?? "conditions de l'accord-cadre 2027"}
              </button>
            ) : (
              cguLinkText ?? "conditions de l'accord-cadre 2027"
            )}
            .<span className="cgu-required-star" aria-hidden="true"> *</span>
          </span>
        </label>

        {/* Aperçu PDF inline */}
        <div id="pdf-preview-zone">
          {isGeneratingPdf && (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: '#6b7280' }}>
              <span className="bottom-save-spinner" style={{ display: 'inline-block', marginRight: '0.5rem' }} />
              Génération du contrat en cours…
            </div>
          )}
          {pdfUrl && !isGeneratingPdf && (
            <div style={{ marginTop: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontWeight: 600, color: '#374151' }}>Aperçu du contrat</span>
                <Button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = pdfUrl;
                    link.download = 'candidature.pdf';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }}
                  icon={<DownloadIcon className="dl-button-icon-white" />}
                  value="Télécharger"
                  size="small"
                />
              </div>
              <iframe src={pdfUrl} title="PDF candidature" width="100%" height="500px" style={{ display: 'block', border: 'none' }} />
            </div>
          )}
        </div>


      </div>
    </>
  );
};

export default SectionSoumission;
